import { Action, createReducer, on } from "@ngrx/store";
import { getKey } from "pax-ui/core";

import { InvalidOperationException } from "../../exceptions/exceptions";

import * as NotificationAction from "./notification.actions";
import {
  CreateIfNotFoundNotificationParameters,
  NotificationMessagePayload,
} from "./notification.actions";
import {
  Code,
  DEFAULT_NOTIFICATION_STATE,
  NotificationFeatureState,
  NotificationMessage,
} from "./notification.state";

const reducer = createReducer(
  DEFAULT_NOTIFICATION_STATE,

  ///////////////////////////////////////////////////////////////////////////////
  // Business Actions
  ///////////////////////////////////////////////////////////////////////////////

  on(
    NotificationAction.createNotifications,
    (state, createNotificationsParameters) =>
      addNotificationMessages(
        state,
        createNotificationsParameters.notificationMessagePayloads,
      ),
  ),

  on(
    NotificationAction.createNotificationIfNotFound,
    (state, createNotificationIfNotFound) =>
      addNotificationMessageIfNotFound(state, createNotificationIfNotFound),
  ),

  on(
    NotificationAction.tryDeleteNotification,
    (state, deleteNotificationParameters) => ({
      ...state,
      notificationMessages: tryDeleteNotificationMessage(
        state.notificationMessages,
        deleteNotificationParameters.notificationId,
      ),
    }),
  ),

  on(
    NotificationAction.tryDeleteNotifications,
    (state, tryDeleteNotificationParameters) => ({
      ...state,
      notificationMessages: tryDeleteNotificationMessages(
        state.notificationMessages,
        tryDeleteNotificationParameters.code,
      ),
    }),
  ),

  on(
    NotificationAction.tryDeleteDeleteAfterNextNavigationNotificationMessages,
    (state) => ({
      ...state,
      notificationMessages:
        tryDeleteDeleteAfterNextNavigationNotificationMessages(
          state.notificationMessages,
        ),
    }),
  ),

  ///////////////////////////////////////////////////////////////////////////////
  // Chain Reaction Actions
  ///////////////////////////////////////////////////////////////////////////////
);

function addNotificationMessages(
  existingNotificationFeatureState: NotificationFeatureState,
  notificationMessagePayloads: NotificationMessagePayload[],
) {
  let nextNotificationMessageId =
    existingNotificationFeatureState.nextNotificationMessageId;

  const newNotificationMessages: NotificationMessage[] = [];

  for (const notificationMessagePayload of notificationMessagePayloads) {
    ensureNotificationMessageIsValid(notificationMessagePayload);

    const newNotificationMessage: NotificationMessage = {
      id: nextNotificationMessageId++,
      code: notificationMessagePayload.code,
      text: notificationMessagePayload.text,
      markdown: notificationMessagePayload.markdown,
      severity: notificationMessagePayload.severity,
      deleteAfterNextNavigation:
        notificationMessagePayload.deleteAfterNextNavigation ?? false,
      deleteAfterNumberOfMilliseconds:
        notificationMessagePayload.deleteAfterNumberOfMilliseconds,
    };

    newNotificationMessages.push(newNotificationMessage);
  }

  const newNotificationFeatureState: NotificationFeatureState = {
    ...existingNotificationFeatureState,
    nextNotificationMessageId: nextNotificationMessageId,
    notificationMessages: [
      ...(existingNotificationFeatureState.notificationMessages ?? []),
      ...newNotificationMessages,
    ],
  };

  return newNotificationFeatureState;
}

function ensureNotificationMessageIsValid(
  notificationMessagePayload: NotificationMessagePayload,
) {
  if (
    notificationMessagePayload.markdown != undefined &&
    notificationMessagePayload.text !== undefined
  ) {
    throw new InvalidOperationException(
      `The properties '${getKey<NotificationMessagePayload>(
        "text",
      )}' and '${getKey<NotificationMessagePayload>(
        "markdown",
      )}' are mutually exclusive for notification messages`,
    );
  }
}

function addNotificationMessageIfNotFound(
  existingNotificationFeatureState: NotificationFeatureState,
  createIfNotFoundNotificationParameters: CreateIfNotFoundNotificationParameters,
) {
  const canNotificationBeFound =
    existingNotificationFeatureState.notificationMessages?.some(
      (n) =>
        n.code ===
        createIfNotFoundNotificationParameters.notificationMessagePayload.code,
    );

  if (!canNotificationBeFound) {
    return addNotificationMessages(existingNotificationFeatureState, [
      createIfNotFoundNotificationParameters.notificationMessagePayload,
    ]);
  }

  // The notification message with the exact 'code' already exists and we can simply return the existing state
  return existingNotificationFeatureState;
}

function tryDeleteNotificationMessage(
  notificationMessages: NotificationMessage[] | undefined,
  notificationMessageId: number,
) {
  // Maybe the user has already clicked the notification message away before the timer could trigger
  if (notificationMessages === undefined) {
    return;
  }

  const filteredNotificationMessages = notificationMessages.filter(
    (notificationMessage) => notificationMessage.id !== notificationMessageId,
  );

  return filteredNotificationMessages;
}

function tryDeleteNotificationMessages(
  notificationMessages: NotificationMessage[] | undefined,
  code: Code,
) {
  const filteredNotificationMessages = notificationMessages?.filter(
    (notificationMessage) => notificationMessage.code !== code,
  );

  return filteredNotificationMessages;
}

function tryDeleteDeleteAfterNextNavigationNotificationMessages(
  notificationMessages: NotificationMessage[] | undefined,
) {
  const filteredNotificationMessages = notificationMessages?.filter(
    (notificationMessage) => !notificationMessage.deleteAfterNextNavigation,
  );

  return filteredNotificationMessages;
}

export function featureReducer(
  state: NotificationFeatureState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
